import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import NewsCard from "@/components/base/NewsCard";
import WorkCard from "@/components/base/WorkCard";
import { newsList } from "@/mocks/news";
import { worksList } from "@/mocks/works";

type TabKey = "all" | "news" | "works";

const tabs: { key: TabKey; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "news", label: "资讯" },
  { key: "works", label: "作品" },
];

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const urlKeyword = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(urlKeyword);
  const [tab, setTab] = useState<TabKey>("all");

  useEffect(() => {
    setKeyword(urlKeyword);
  }, [urlKeyword]);

  const query = keyword.trim().toLowerCase();

  const matchedNews = useMemo(() => {
    if (!query) return [];
    return newsList.filter(
      (item) =>
        item.title.toLowerCase().includes(query) ||
        item.summary.toLowerCase().includes(query) ||
        item.category.includes(keyword.trim())
    );
  }, [query, keyword]);

  const matchedWorks = useMemo(() => {
    if (!query) return [];
    return worksList.filter(
      (item) =>
        item.title.toLowerCase().includes(query) ||
        item.synopsis.toLowerCase().includes(query) ||
        item.role.includes(keyword.trim()) ||
        item.director.includes(keyword.trim())
    );
  }, [query, keyword]);

  const totalCount = matchedNews.length + matchedWorks.length;
  const showNews = tab === "all" || tab === "news";
  const showWorks = tab === "all" || tab === "works";

  const hotNews = [...newsList].sort((a, b) => b.views - a.views).slice(0, 4);
  const hotWorks = [...worksList].sort((a, b) => b.rating - a.rating).slice(0, 4);

  return (
    <SiteLayout
      title="搜索白石茉莉奈资讯与作品 - 明星动态平台"
      description="搜索白石茉莉奈相关资讯与影视作品，输入关键词即可快速匹配资讯标题与作品名称，支持分类切换查看。"
      keywords="搜索白石茉莉奈,白石茉莉奈资讯搜索,白石茉莉奈作品搜索"
    >
      <div className="bg-primary-950">
        <div className="px-4 md:px-8 py-10 md:py-16">
          <h1 className="font-heading text-2xl md:text-4xl text-background-50 tracking-wide">
            全站搜索
          </h1>
          <p className="text-sm md:text-base text-foreground-300 mt-3 font-body">
            输入关键词，快速查找白石茉莉奈的资讯与影视作品。
          </p>
          <div className="relative mt-6 max-w-2xl">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索资讯标题、作品名称、角色或标签…"
              className="w-full text-base font-body bg-background-50 border border-transparent rounded-md pl-11 pr-4 py-3.5 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-400"
            />
          </div>
        </div>
      </div>

      <div className="px-4 md:px-8 pt-6">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />
      </div>

      <div className="px-4 md:px-8 py-8 md:py-10">
        {query ? (
          <>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <p className="text-sm text-foreground-500 font-body">
                关键词「
                <span className="text-primary-600 font-semibold">{keyword.trim()}</span>
                」共找到
                <span className="text-foreground-900 font-semibold"> {totalCount} </span>
                条结果
              </p>
              <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                {tabs.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTab(t.key)}
                    className={`px-4 py-1.5 rounded-full text-sm font-body transition-colors cursor-pointer whitespace-nowrap ${
                      tab === t.key
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-600 hover:text-primary-600"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {totalCount === 0 ? (
              <div className="py-10">
                <div className="text-center mb-10">
                  <div className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center mx-auto mb-5">
                    <i className="ri-search-eye-line text-2xl text-foreground-400"></i>
                  </div>
                  <h3 className="font-heading text-lg text-foreground-800">
                    没有找到相关内容
                  </h3>
                  <p className="text-sm text-foreground-500 mt-2 font-body">
                    换个关键词试试，或者看看下面的热门推荐。
                  </p>
                </div>

                <h4 className="font-heading text-base text-foreground-900 mb-4 flex items-center gap-2">
                  <span className="w-1 h-5 rounded-full bg-accent-500"></span>
                  热门资讯推荐
                </h4>
                <div className="grid grid-cols-1 gap-4 mb-10">
                  {hotNews.map((item) => (
                    <NewsCard key={item.id} item={item} />
                  ))}
                </div>

                <h4 className="font-heading text-base text-foreground-900 mb-4 flex items-center gap-2">
                  <span className="w-1 h-5 rounded-full bg-accent-500"></span>
                  热门作品推荐
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                  {hotWorks.map((item) => (
                    <WorkCard key={item.id} item={item} />
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-10">
                {showNews && matchedNews.length > 0 && (
                  <section>
                    <h2 className="font-heading text-base text-foreground-900 mb-4 flex items-center gap-2">
                      <i className="ri-newspaper-line text-secondary-500"></i>
                      相关资讯（{matchedNews.length}）
                    </h2>
                    <div className="grid grid-cols-1 gap-4 md:gap-5">
                      {matchedNews.map((item) => (
                        <NewsCard key={item.id} item={item} />
                      ))}
                    </div>
                  </section>
                )}

                {showWorks && matchedWorks.length > 0 && (
                  <section>
                    <h2 className="font-heading text-base text-foreground-900 mb-4 flex items-center gap-2">
                      <i className="ri-film-line text-accent-500"></i>
                      相关作品（{matchedWorks.length}）
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                      {matchedWorks.map((item) => (
                        <WorkCard key={item.id} item={item} />
                      ))}
                    </div>
                  </section>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="py-6">
            <div className="text-center mb-10">
              <div className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center mx-auto mb-5">
                <i className="ri-search-2-line text-2xl text-foreground-400"></i>
              </div>
              <h3 className="font-heading text-lg text-foreground-800">
                输入关键词开始搜索
              </h3>
              <p className="text-sm text-foreground-500 mt-2 font-body">
                例如：资讯、作品名称、角色名、导演或标签。
              </p>
            </div>

            <h4 className="font-heading text-base text-foreground-900 mb-4 flex items-center gap-2">
              <span className="w-1 h-5 rounded-full bg-accent-500"></span>
              热门资讯
            </h4>
            <div className="grid grid-cols-1 gap-4 mb-10">
              {hotNews.map((item) => (
                <NewsCard key={item.id} item={item} />
              ))}
            </div>

            <h4 className="font-heading text-base text-foreground-900 mb-4 flex items-center gap-2">
              <span className="w-1 h-5 rounded-full bg-accent-500"></span>
              热门作品
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
              {hotWorks.map((item) => (
                <WorkCard key={item.id} item={item} />
              ))}
            </div>

            <div className="text-center mt-10">
              <Link
                to="/news"
                className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-body"
              >
                浏览全部资讯
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}